
import React from "react";
import { useTaskDetailModal } from "./TaskDetailModalContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import {
  getCardBaseClasses,
  getCardHeaderClasses,
  getCardTitleClasses,
  getCardMetadataClasses,
} from "@/components/ui/shared-card-styles";
import { cn } from "@/lib/utils";
import { PriorityBadge } from "./PriorityBadge";

// Helper to format minutes to "1h 25m"
function formatMinutes(minutes: number) {
  if (!minutes) return "0m";
  const hrs = Math.floor(minutes / 60);
  const min = minutes % 60;
  return `${hrs ? `${hrs}h ` : ""}${min}m`.trim();
}

export const TaskDetailModal: React.FC = () => {
  const { isOpen, close, task } = useTaskDetailModal();

  if (!isOpen || !task) return null;

  return (
    <Dialog open={isOpen} onOpenChange={open => { if (!open) close(); }}>
      <DialogContent className="bg-transparent border-0 shadow-none max-w-xl p-3">
        <div
          className={cn(
            getCardBaseClasses(false),
            "relative p-6 rounded-2xl max-w-xl w-full"
          )}
        >
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 text-muted-foreground hover:text-blue-600"
          >
            <X size={24} />
          </button>
          <div className={getCardHeaderClasses()}>
            <DialogTitle className={cn(getCardTitleClasses(), "text-3xl font-extrabold mb-2 leading-tight")}>
              {task.title || "Untitled Task"}
            </DialogTitle>
          </div>
          
          {/* Priority Badge — replaces status */}
          {task.priority_quadrant && (
            <div className="mb-4">
              <PriorityBadge quadrant={task.priority_quadrant} />
            </div>
          )}

          {/* Description */}
          {(task.detail || task.description) && (
            <div className="mb-4 text-sm text-foreground whitespace-pre-line">
              {task.detail || task.description}
            </div>
          )}
          {/* Pomodoros & Time */}
          <div className={cn(getCardMetadataClasses(), "gap-8 pt-2 border-t border-gray-100 dark:border-gray-700 mt-3")}>
            <div>
              <span className="block text-xs text-muted-foreground">Pomodoros Completed</span>
              <span className="font-semibold">{task.pomodorosCompleted ?? task.pomodoros_completed ?? 0}</span>
            </div>
            <div>
              <span className="block text-xs text-muted-foreground">Total Time Spent</span>
              <span className="font-semibold">
                {typeof task.totalTimeSpent === "number"
                  ? formatMinutes(task.totalTimeSpent)
                  : formatMinutes(task.total_time_spent || 0)}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
